import { Controller } from "@hotwired/stimulus"

// Keeps the board shell and selection alive while individual columns change.
export default class extends Controller {
  static values = { columnUrl: String }
  static targets = ["status", "retry"]

  connect() {
    this.abortController = new AbortController()
    this.refreshWaiters = []
    this.boundRefresh = () => this.refresh()
    document.addEventListener("board:refresh", this.boundRefresh)
  }

  disconnect() {
    document.removeEventListener("board:refresh", this.boundRefresh)
    this.abortController?.abort()
    this.refreshWaiters.splice(0).forEach(resolve => resolve(false))
  }

  pageUrl(key, page) {
    const url = new URL(window.location.href)
    const pages = new Map((url.searchParams.get("pages") || "").split(",").filter(Boolean).map(value => value.split(":")))
    pages.set(key, String(page))
    url.searchParams.set("pages", Array.from(pages, ([key, page]) => `${key}:${page}`).join(","))
    return url
  }

  columnUrl(key, boardUrl = new URL(window.location.href)) {
    const url = new URL(this.columnUrlValue.replace("__column__", encodeURIComponent(key)), window.location.origin)
    url.search = boardUrl.search
    return url
  }

  async paginate(event) {
    const link = event.target.closest("a")
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    event.stopPropagation()
    if (this.busy) return
    const frame = link.closest("turbo-frame")
    const key = frame.dataset.boardColumnKey
    const requested = new URL(link.href)
    const page = new Map(requested.searchParams.get("pages").split(",").map(value => value.split(":"))).get(key)
    this.busy = true
    this.message("Loading column…")
    try {
      await this.loadColumn(frame, this.columnUrl(key, this.pageUrl(key, page)), false)
      this.message("")
      frame.querySelector('nav [aria-current="page"]')?.focus({ preventScroll: true })
    } catch (error) {
      if (error.name !== "AbortError") this.message(error.message, true)
    } finally {
      this.busy = false
      this.flushRefresh()
    }
  }

  async loadColumn(frame, url, preserveScroll = true) {
    const top = preserveScroll ? frame.scrollTop : 0
    const focused = frame.contains?.(document.activeElement) ? document.activeElement : null
    const reference = focused?.closest('.kanban-card')?.dataset.reference
    const focusSelector = focused?.matches('input[type="checkbox"]') ? 'input[type="checkbox"]' : 'a'
    frame.setAttribute("aria-busy", "true")
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, signal: this.abortController?.signal })
      if (!response.ok) throw new Error("The column could not be loaded. Try again.")
      const html = new DOMParser().parseFromString(await response.text(), "text/html")
      const incoming = html.getElementById(frame.id)
      if (!incoming) throw new Error("The column could not be loaded. Refresh or sign in again.")
      if (this.abortController?.signal.aborted) return
      frame.removeAttribute("src")
      frame.replaceChildren(...incoming.childNodes)
      frame.scrollTop = top
      this.recordPage(frame)
      if (reference) {
        Array.from(frame.querySelectorAll('.kanban-card')).find(card => card.dataset.reference === reference)
          ?.querySelector(focusSelector)?.focus({ preventScroll: true })
      }
    } finally {
      frame.removeAttribute("aria-busy")
    }
  }

  recordPage(frame) {
    const page = frame.querySelector('[data-board-page]')?.dataset.boardPage
    if (!page) return
    const url = this.pageUrl(frame.dataset.boardColumnKey, page)
    window.history.replaceState(window.history.state, "", url)
  }

  frameLoaded(event) {
    if (event.target.matches('turbo-frame[data-board-column-key]')) this.recordPage(event.target)
  }

  async refresh() {
    if (this.busy) {
      return new Promise(resolve => this.refreshWaiters.push(resolve))
    }
    this.busy = true
    this.message("Updating board…")
    try {
      // The shell query refreshes counts for collapsed lists too. Only columns
      // already loaded fetch cards; unopened columns remain lazy.
      const response = await fetch(window.location.href, { headers: { Accept: "text/html" }, signal: this.abortController.signal })
      if (!response.ok) throw new Error("The board could not be refreshed. Try again.")
      const html = new DOMParser().parseFromString(await response.text(), "text/html")
      if (!html.querySelector('[data-controller~="board-work"]')) throw new Error("The board could not be refreshed. Refresh or sign in again.")
      this.element.querySelectorAll('[data-board-refresh-key]').forEach(element => {
        const incoming = Array.from(html.querySelectorAll('[data-board-refresh-key]'))
          .find(candidate => candidate.dataset.boardRefreshKey === element.dataset.boardRefreshKey)
        if (incoming) element.replaceChildren(...incoming.childNodes)
      })
      const frames = Array.from(this.element.querySelectorAll('turbo-frame[data-board-column-key]'))
        .filter(frame => frame.querySelector('[data-board-page]'))
      const results = await Promise.allSettled(frames.map(frame => this.loadColumn(frame, this.columnUrl(frame.dataset.boardColumnKey))))
      if (results.some(result => result.status === "rejected")) throw new Error("Some columns could not be refreshed. Try again.")
      this.message("Board updated")
      return true
    } catch (error) {
      if (error.name !== "AbortError") this.message(error.message, true)
      return false
    } finally {
      this.busy = false
      document.dispatchEvent(new CustomEvent("board:refreshed"))
      this.flushRefresh()
    }
  }

  flushRefresh() {
    if (!this.refreshWaiters.length) return
    const waiters = this.refreshWaiters.splice(0)
    this.refresh().then(result => waiters.forEach(resolve => resolve(result)))
  }

  message(text, failed = false) {
    this.statusTarget.textContent = text
    this.retryTarget.classList.toggle("hidden", !failed)
  }
}
